import { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { authService } from '@/services/authService';
import { LockClosedIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useTranslation } from '@/i18n/useTranslation';
import type { AuthOutletContext } from '@/layouts/AuthLayout';

interface ForgotPasswordForm {
  email: string;
  user_type?: string;
  shop_id?: number;
}

type ShopOption = {
  shop_id: number;
  shop_name: string;
  shop_code?: string | null;
};

const ForgotPassword = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [shopOptions, setShopOptions] = useState<ShopOption[] | null>(null);
  const { t, locale } = useTranslation('common');
  const { shopBranding } = useOutletContext<AuthOutletContext>();
  const shopQuery = shopBranding?.shopQuery || '';

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm<ForgotPasswordForm>();

  const email = watch('email');
  const userType = watch('user_type') || 'customer';
  const selectedShopId = watch('shop_id');
  const loginPath =
    userType === 'staff'
      ? '/auth/staff/login'
      : `/auth/customer/login${shopQuery}`;

  const onSubmit = async (data: ForgotPasswordForm) => {
    try {
      setIsSubmitting(true);
      const shopId =
        data.shop_id != null && Number.isFinite(Number(data.shop_id))
          ? Number(data.shop_id)
          : undefined;

      const response = await authService.forgotPassword(
        data.email,
        data.user_type || 'customer',
        locale,
        shopId
      );

      if (response.success && response.data?.shop_selection_required && response.data.shops?.length) {
        setShopOptions(response.data.shops);
        toast(t('auth.forgotPasswordPage.selectShopPrompt'));
        return;
      }

      if (response.success) {
        setIsSubmitted(true);
        setShopOptions(null);
        toast.success(t('auth.forgotPasswordPage.toastSent'));
      } else {
        toast.error(response.message || t('auth.forgotPasswordPage.toastFailed'));
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      // Avoid leaking whether the account exists
      setIsSubmitted(true);
      setShopOptions(null);
      toast.success(t('auth.forgotPasswordPage.toastSent'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <EnvelopeIcon className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {t('auth.forgotPasswordPage.checkEmailTitle')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth.forgotPasswordPage.sentTo')}
          </p>
          <p className="mt-1 text-sm font-medium text-gray-900">{email}</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <EnvelopeIcon className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                {t('auth.forgotPasswordPage.emailSentTitle')}
              </h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>{t('auth.forgotPasswordPage.emailSentBody')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            {t('auth.forgotPasswordPage.didntReceive')}{' '}
            <button
              onClick={() => setIsSubmitted(false)}
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              {t('auth.forgotPasswordPage.tryAgain')}
            </button>
          </p>
          <p className="mt-4 text-sm text-gray-600">
            {t('auth.forgotPasswordPage.rememberPassword')}{' '}
            <Link to={loginPath} className="font-medium text-primary-600 hover:text-primary-500">
              {t('auth.forgotPasswordPage.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md w-full space-y-8">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100">
          <LockClosedIcon className="h-6 w-6 text-primary-600" />
        </div>
        <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
          {t('auth.forgotPasswordPage.title')}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {t('auth.forgotPasswordPage.subtitle')}
        </p>
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label htmlFor="user_type" className="form-label">
            {t('auth.forgotPasswordPage.accountType')} *
          </label>
          <select
            {...register('user_type', {
              required: t('auth.forgotPasswordPage.accountTypeRequired'),
              onChange: () => {
                setShopOptions(null);
                setValue('shop_id', undefined);
              },
            })}
            id="user_type"
            className={`input ${errors.user_type ? 'input-error' : ''}`}
            defaultValue="customer"
          >
            <option value="customer">{t('auth.forgotPasswordPage.accountTypeCustomer')}</option>
            <option value="staff">{t('auth.forgotPasswordPage.accountTypeStaff')}</option>
          </select>
          {errors.user_type && (
            <p className="form-error mt-1">{errors.user_type.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="form-label">
            {t('auth.forgotPasswordPage.emailRequiredLabel')}
          </label>
          <input
            {...register('email', {
              required: t('auth.validation.emailRequired'),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('auth.validation.invalidEmail'),
              },
            })}
            id="email"
            type="email"
            autoComplete="email"
            className={`input ${errors.email ? 'input-error' : ''}`}
            placeholder={t('auth.forgotPasswordPage.placeholderEmailLong')}
          />
          {errors.email && (
            <p className="form-error mt-1">{errors.email.message}</p>
          )}
        </div>

        {shopOptions && shopOptions.length > 0 && (
          <div>
            <label htmlFor="shop_id" className="form-label">
              {t('auth.forgotPasswordPage.selectShop')}
            </label>
            <select
              id="shop_id"
              className="input"
              value={selectedShopId ?? ''}
              onChange={(e) => {
                const value = e.target.value ? Number(e.target.value) : undefined;
                setValue('shop_id', value, { shouldValidate: true });
              }}
              required
            >
              <option value="">{t('auth.forgotPasswordPage.selectShopPlaceholder')}</option>
              {shopOptions.map((shop) => (
                <option key={shop.shop_id} value={shop.shop_id}>
                  {shop.shop_name}
                  {shop.shop_code ? ` (${shop.shop_code})` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {t('auth.forgotPasswordPage.selectShopHint')}
            </p>
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={isSubmitting || (Boolean(shopOptions?.length) && !selectedShopId)}
            className="btn btn-primary w-full flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner className="w-5 h-5 mr-2" />
                {t('auth.forgotPasswordPage.sending')}
              </>
            ) : (
              <>
                <EnvelopeIcon className="w-5 h-5 mr-2" />
                {t('auth.forgotPasswordPage.sendResetInstructions')}
              </>
            )}
          </button>
        </div>

        <div className="text-center">
          <Link
            to={loginPath}
            className="font-medium text-primary-600 hover:text-primary-500"
          >
            {t('auth.forgotPasswordPage.backToLogin')}
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ForgotPassword;
